import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { ScheduledCallCreatePopup } from 'APP/main/PopupManager/Popups/ScheduledCalls/ScheduledCallCreatePopup/ScheduledCallCreatePopup';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';
import { BackgroundCall } from 'STORE/BackgroundCalls/BackgroundCall/BackgroundCall';
import { MainCall } from 'STORE/Calls/MainCall/MainCall';
import { Channel } from 'STORE/Groups/Channel';
import { Group } from 'STORE/Groups/Group';

interface IDefaultHeaderActionsPresenter {
  call: BackgroundCall | null;
  isAvailableConference: boolean;
  isAvailableJoinCall: boolean;
  isShowSearch: boolean;
  isArticleDrafts: boolean;
  isGroupJoinButton: boolean;
  isCreateCallButton: boolean;
  isAvailableStartStream: boolean;
  isStreamStarting: boolean;
  hasDonateButton: boolean;
  activeCall: MainCall | null;
  backgroundCall: BackgroundCall | null;
  isAvailableSearch: boolean;
  audioCall(): void;
  conferenceCall(): void;
  joinToCall(): void;
  startStream(): Promise<void>;
  showSearch(): void;
  onToggleMute(): void;
  scheduleCallEvent(): void;
}

export const useDefaultHeaderActionsPresenter = (group: Group): IDefaultHeaderActionsPresenter => {
  const source = useAsObservableSource({ group });
  const { t } = useTranslation();
  const presenter = useLocalStore<IDefaultHeaderActionsPresenter>(() => ({
    isStreamStarting: false,

    get call(): BackgroundCall | null {
      return Entities.BackgroundCalls.getCallByGroupId(source.group.id);
    },

    get isAvailableConference(): boolean {
      return !source.group.isP2P && source.group.withMeInAdmins && !source.group.isSecretGroup;
    },

    get isAvailableJoinCall(): boolean {
      const activeCall = Entities.Calls.getCallByGroupId(source.group.id);
      if (activeCall) {
        return false;
      }

      return Boolean(source.group.backgroundCall);
    },

    get activeCall(): MainCall | null {
      return source.group.activeCall || null;
    },

    get backgroundCall(): BackgroundCall | null {
      return source.group.backgroundCall;
    },

    get isShowSearch(): boolean {
      return Entities.ChatStore.isShowSearch;
    },

    get isAvailableSearch(): boolean {
      return !source.group.isSecretGroup;
    },

    get isArticleDrafts(): boolean {
      return (
        Boolean(source.group.isChannel && source.group.withMeInAdmins) &&
        Features.isArticleEnabled()
      );
    },

    get isGroupJoinButton(): boolean {
      return source.group.withoutMe;
    },

    get isCreateCallButton(): boolean {
      return (
        !source.group.isChannel &&
        !source.group.isBot &&
        !source.group.isSavedMessages &&
        !source.group.activeCall &&
        !source.group.isSecretGroup &&
        !source.group.isServiceMessages
      );
    },

    get isAvailableStartStream(): boolean {
      if (
        !source.group.isChannel ||
        !source.group.withMeInAdmins ||
        Entities.BackgroundStreams.getByGroupId(source.group.id) ||
        !Features.isStreamsEnabled() ||
        source.group.isSecretGroup
      ) {
        return false;
      }

      return true;
    },

    get hasDonateButton(): boolean {
      return Boolean(
        source.group instanceof Channel && !source.group.withMeInAdmins && source.group.donationUrl
      );
    },

    audioCall(): void {
      Tasks.calls.startAudioCall({ groupId: source.group.id });
    },

    conferenceCall(): void {
      Tasks.calls.startConferenceCall({ groupId: source.group.id });
    },

    joinToCall(): void {
      if (!presenter.call) {
        return;
      }

      Tasks.calls.joinToCall({
        groupId: source.group.id,
        channelId: presenter.call.channelId,
        withAudio: !!source.group.isP2P,
        withVideo: false,
        providerType: presenter.call.callProvider,
      });
    },

    async startStream(): Promise<void> {
      if (presenter.isStreamStarting) {
        return;
      }

      presenter.isStreamStarting = true;
      await Tasks.streaming.openCreateStreamPopup(source.group.id);
      presenter.isStreamStarting = false;
    },

    scheduleCallEvent(): void {
      if (!source.group.canInitCall) {
        Tasks.app.showAlert(t('no_permission_alert'));

        return;
      }
      Tasks.app.openPopup(ScheduledCallCreatePopup, { params: { groupId: source.group.id } });
    },

    showSearch(): void {
      Entities.ChatStore.showSearch();
    },

    onToggleMute(): void {
      Tasks.group.toggleNotifications(source.group.id);
    },
  }));

  return presenter;
};
